import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Settings, AlertCircle, Loader2, TrendingUp, TrendingDown, Minus, CheckCircle2, Sparkles, Download, FileText, FileDown, ChevronDown, ChevronUp, Users, MapPin } from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import FunnelChart from '../components/FunnelChart';
import MainKPICard from '../components/MainKPICard';
import MetricHelpIcon from '../components/MetricHelpIcon';
import { useSortableData } from '../hooks/useSortableData';
import SortableTableHeader from '../components/SortableTableHeader';
import DateRangePicker, { DATE_RANGE_PRESETS } from '../components/DateRangePicker';
import { ReportSlot } from '../components/ReportSlot';
import { usePortalReports, currentMonthRange, rangeKey } from '../portal/PortalReportsContext';
import { PortalReportsStatus } from '../portal/PortalReportsStatus';
import { RefreshCw } from 'lucide-react';

const PORTAL_PRESETS = [{ value: 'mes_atual', label: 'Mês atual' }, ...DATE_RANGE_PRESETS];
const fmtBr = (ymd) => (ymd ? ymd.split('-').reverse().join('/') : '');
const fmtBrDateTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};
import ReactMarkdown from 'react-markdown';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Markdown components for ReactMarkdown (defined outside render to avoid recreation)
const markdownComponents = {
  h1: (props) => <h2 className="text-lg font-bold mt-4 mb-2 text-foreground" {...props} />,
  h2: (props) => <h3 className="text-base font-semibold mt-4 mb-2 text-foreground" {...props} />,
  h3: (props) => <h4 className="text-sm font-semibold mt-3 mb-1 text-foreground" {...props} />,
  p: (props) => <p className="mb-2 text-sm text-muted-foreground leading-relaxed" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />,
  li: (props) => <li className="text-sm text-muted-foreground" {...props} />,
  strong: (props) => <strong className="font-semibold text-foreground" {...props} />
};

// Helper function to format seconds as mm:ss
const formatTime = (seconds) => {
  if (!seconds) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Default limit for table rows
const DEFAULT_TABLE_LIMIT = 10;

// View More/Less Button Component
const ViewMoreButton = ({ isExpanded, onClick, totalItems, limit = DEFAULT_TABLE_LIMIT }) => {
  if (totalItems <= limit) return null;
  
  return (
    <div className="flex justify-center pt-4 border-t border-border/40 mt-4">
      <button
        onClick={onClick}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        {isExpanded ? (
          <>
            <ChevronUp className="h-4 w-4" />
            Ver menos
          </>
        ) : (
          <>
            <ChevronDown className="h-4 w-4" />
            Ver mais ({totalItems - limit} itens)
          </>
        )}
      </button>
    </div>
  );
};

// Column type definitions for sorting
const DEVICE_COLUMN_TYPES = {
  device_category: 'text',
  sessions: 'number',
  users: 'number',
  new_users: 'number',
  conversion_rate: 'number',
  bounce_rate: 'number',
  avg_engagement_time_seconds: 'number'
};

const BROWSER_COLUMN_TYPES = {
  browser: 'text',
  sessions: 'number',
  users: 'number',
  new_users: 'number',
  conversion_rate: 'number',
  bounce_rate: 'number',
  avg_engagement_time_seconds: 'number'
};

const SOURCE_MEDIUM_COLUMN_TYPES = {
  source_medium: 'text',
  sessions: 'number',
  users: 'number',
  new_users: 'number',
  conversion_rate: 'number',
  bounce_rate: 'number',
  avg_engagement_time_seconds: 'number'
};

const TOP_PAGES_COLUMN_TYPES = {
  page_path: 'text',
  views: 'number',
  sessions: 'number',
  avg_engagement_time: 'number',
  conversion_rate: 'number',
  bounce_rate: 'number'
};

// Device Breakdown Table Component with Sorting and View More/Less
const DeviceBreakdownTable = ({ devices, formatTime, isExpanded, onToggleExpand }) => {
  const { sortedData, sortColumn, sortDirection, handleSort } = useSortableData(
    devices,
    { columnTypes: DEVICE_COLUMN_TYPES }
  );

  const deviceNames = {
    mobile: 'Mobile',
    desktop: 'Desktop',
    tablet: 'Tablet'
  };

  const displayData = isExpanded ? sortedData : sortedData.slice(0, DEFAULT_TABLE_LIMIT);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resumo de desempenho por dispositivo</CardTitle>
        <CardDescription>Métricas detalhadas de UX e usabilidade por tipo de dispositivo</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <SortableTableHeader
                  column="device_category"
                  label="Dispositivo"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="left"
                />
                <SortableTableHeader
                  column="sessions"
                  label="Sessões"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="users"
                  label="Usuários"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="new_users"
                  label="Novos usuários"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="conversion_rate"
                  label="Taxa de conversão"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="bounce_rate"
                  label="Taxa de rejeição"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="avg_engagement_time_seconds"
                  label="Tempo médio de engajamento"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {displayData.map((device) => (
                <tr key={device.device_category} className="border-b hover:bg-muted/50">
                  <td className="py-3 px-4 font-medium">
                    {deviceNames[device.device_category] || device.device_category}
                  </td>
                  <td className="text-right py-3 px-4">
                    {(device.sessions || 0).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-right py-3 px-4">
                    {(device.users || 0).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-right py-3 px-4">
                    {(device.new_users || 0).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-right py-3 px-4 font-semibold" style={{ color: 'hsl(var(--primary))' }}>
                    {((device.conversion_rate || 0) * 100).toFixed(1).replace('.', ',')}%
                  </td>
                  <td className="text-right py-3 px-4 font-semibold text-red-600">
                    {((device.bounce_rate || 0) * 100).toFixed(1).replace('.', ',')}%
                  </td>
                  <td className="text-right py-3 px-4">
                    {formatTime(device.avg_engagement_time_seconds || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ViewMoreButton 
          isExpanded={isExpanded} 
          onClick={onToggleExpand}
          totalItems={sortedData.length}
        />
      </CardContent>
    </Card>
  );
};

// Browser Breakdown Table Component with Sorting and View More/Less
const BrowserBreakdownTable = ({ browsers, formatTime, isExpanded, onToggleExpand }) => {
  const { sortedData, sortColumn, sortDirection, handleSort } = useSortableData(
    browsers || [],
    { columnTypes: BROWSER_COLUMN_TYPES }
  );

  if (!browsers || browsers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Resumo de desempenho por navegador</CardTitle>
          <CardDescription>Métricas detalhadas de UX e usabilidade por navegador</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-6">
            Não há dados suficientes sobre navegadores para este período.
          </p>
        </CardContent>
      </Card>
    );
  }

  const displayData = isExpanded ? sortedData : sortedData.slice(0, DEFAULT_TABLE_LIMIT);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resumo de desempenho por navegador</CardTitle>
        <CardDescription>Métricas detalhadas de UX e usabilidade por navegador</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <SortableTableHeader
                  column="browser"
                  label="Navegador"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="left"
                />
                <SortableTableHeader
                  column="sessions"
                  label="Sessões"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="users"
                  label="Usuários"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="new_users"
                  label="Novos usuários"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="conversion_rate"
                  label="Taxa de conversão"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="bounce_rate"
                  label="Taxa de rejeição"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="avg_engagement_time_seconds"
                  label="Tempo médio de engajamento"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {displayData.map((browser) => (
                <tr key={browser.browser} className="border-b hover:bg-muted/50">
                  <td className="py-3 px-4 font-medium">
                    {browser.browser}
                  </td>
                  <td className="text-right py-3 px-4">
                    {(browser.sessions || 0).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-right py-3 px-4">
                    {(browser.users || 0).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-right py-3 px-4">
                    {(browser.new_users || 0).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-right py-3 px-4 font-semibold" style={{ color: 'hsl(var(--primary))' }}>
                    {((browser.conversion_rate || 0) * 100).toFixed(1).replace('.', ',')}%
                  </td>
                  <td className="text-right py-3 px-4 font-semibold text-red-600">
                    {((browser.bounce_rate || 0) * 100).toFixed(1).replace('.', ',')}%
                  </td>
                  <td className="text-right py-3 px-4">
                    {formatTime(browser.avg_engagement_time_seconds || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ViewMoreButton 
          isExpanded={isExpanded} 
          onClick={onToggleExpand}
          totalItems={sortedData.length}
        />
      </CardContent>
    </Card>
  );
};

// Source/Medium Breakdown Table Component with Sorting and View More/Less
// Small comparison indicator (current value above, % change vs previous period below)
// Mirrors the visual pattern used in the Visão Geral cards.
const MetricChange = ({ pct, isNew }) => {
  if (pct === null || pct === undefined) {
    return (
      <div className="text-xs mt-0.5 text-muted-foreground" data-testid="metric-change-empty">
        {isNew ? 'novo' : '—'}
      </div>
    );
  }
  const positive = pct > 0;
  const negative = pct < 0;
  const color = positive ? 'text-green-600' : negative ? 'text-red-600' : 'text-muted-foreground';
  const Icon = positive ? TrendingUp : negative ? TrendingDown : Minus;
  return (
    <div className={`text-xs mt-0.5 flex items-center justify-end gap-1 ${color}`} data-testid="metric-change">
      <Icon className="h-3 w-3" />
      <span>{positive ? '+' : ''}{pct.toFixed(1).replace('.', ',')}%</span>
    </div>
  );
};

const SourceMediumBreakdownTable = ({ sourceMediums, formatTime, isExpanded, onToggleExpand }) => {
  const { sortedData, sortColumn, sortDirection, handleSort } = useSortableData(
    sourceMediums || [],
    { columnTypes: SOURCE_MEDIUM_COLUMN_TYPES }
  );

  if (!sourceMediums || sourceMediums.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Resumo de desempenho por origem/mídia</CardTitle>
          <CardDescription>Métricas de UX e usabilidade por origem/mídia de aquisição</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-6">
            Não há dados suficientes sobre origens e mídias para este período.
          </p>
        </CardContent>
      </Card>
    );
  }

  const displayData = isExpanded ? sortedData : sortedData.slice(0, DEFAULT_TABLE_LIMIT);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resumo de desempenho por origem/mídia</CardTitle>
        <CardDescription>Métricas de UX e usabilidade por origem/mídia de aquisição</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <SortableTableHeader
                  column="source_medium"
                  label="Origem / Mídia"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="left"
                />
                <SortableTableHeader
                  column="sessions"
                  label="Sessões"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="users"
                  label="Usuários"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="new_users"
                  label="Novos usuários"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="conversion_rate"
                  label="Taxa de conversão"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="bounce_rate"
                  label="Taxa de rejeição"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="avg_engagement_time_seconds"
                  label="Tempo médio de engajamento"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {displayData.map((source, index) => (
                <tr key={source.source_medium || index} className="border-b hover:bg-muted/50">
                  <td className="py-3 px-4 font-medium">
                    {source.source_medium}
                  </td>
                  <td className="text-right py-3 px-4">
                    <div>{(source.sessions || 0).toLocaleString('pt-BR')}</div>
                    <MetricChange pct={source.sessions_change_pct} isNew={source.is_new_source} />
                  </td>
                  <td className="text-right py-3 px-4">
                    <div>{(source.users || 0).toLocaleString('pt-BR')}</div>
                    <MetricChange pct={source.users_change_pct} isNew={source.is_new_source} />
                  </td>
                  <td className="text-right py-3 px-4">
                    <div>{(source.new_users || 0).toLocaleString('pt-BR')}</div>
                    <MetricChange pct={source.new_users_change_pct} isNew={source.is_new_source} />
                  </td>
                  <td className="text-right py-3 px-4">
                    <div className="font-semibold" style={{ color: 'hsl(var(--primary))' }}>
                      {((source.conversion_rate || 0) * 100).toFixed(1).replace('.', ',')}%
                    </div>
                    <MetricChange pct={source.conversion_rate_change_pct} isNew={source.is_new_source} />
                  </td>
                  <td className="text-right py-3 px-4">
                    <div className="font-semibold text-red-600">
                      {((source.bounce_rate || 0) * 100).toFixed(1).replace('.', ',')}%
                    </div>
                    <MetricChange pct={source.bounce_rate_change_pct} isNew={source.is_new_source} />
                  </td>
                  <td className="text-right py-3 px-4">
                    <div>{formatTime(source.avg_engagement_time_seconds || 0)}</div>
                    <MetricChange pct={source.avg_engagement_time_change_pct} isNew={source.is_new_source} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ViewMoreButton 
          isExpanded={isExpanded} 
          onClick={onToggleExpand}
          totalItems={sortedData.length}
        />
      </CardContent>
    </Card>
  );
};

// Top Pages Table Component with Sorting and View More/Less
const TopPagesTable = ({ topPages, formatTime, isExpanded, onToggleExpand }) => {
  const { sortedData, sortColumn, sortDirection, handleSort } = useSortableData(
    topPages,
    { columnTypes: TOP_PAGES_COLUMN_TYPES }
  );

  if (!topPages || topPages.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Páginas com maior impacto</CardTitle>
          <CardDescription>As páginas mais acessadas e seu desempenho de usabilidade</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-6">
            Não há dados suficientes para este período.
          </p>
        </CardContent>
      </Card>
    );
  }

  const displayData = isExpanded ? sortedData : sortedData.slice(0, DEFAULT_TABLE_LIMIT);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Páginas com maior impacto</CardTitle>
        <CardDescription>As páginas mais acessadas e seu desempenho de usabilidade</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <SortableTableHeader
                  column="page_path"
                  label="Página"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="left"
                />
                <SortableTableHeader
                  column="views"
                  label="Usuários"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="sessions"
                  label="Sessões"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="avg_engagement_time"
                  label="Tempo médio"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="conversion_rate"
                  label="Taxa de conversão"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableTableHeader
                  column="bounce_rate"
                  label="Taxa de rejeição"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
              </tr>
            </thead>
            <tbody>
              {displayData.map((page, idx) => (
                <tr key={idx} className="border-b hover:bg-muted/50">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-sm">{page.page_path}</p>
                    </div>
                  </td>
                  <td className="text-right py-3 px-4 font-medium">
                    {(page.views || 0).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-right py-3 px-4">
                    {(page.sessions || page.views || 0).toLocaleString('pt-BR')}
                  </td>
                  <td className="text-right py-3 px-4">
                    {formatTime(page.avg_engagement_time)}
                  </td>
                  <td className="text-right py-3 px-4 font-semibold" style={{ color: 'hsl(var(--primary))' }}>
                    {((page.conversion_rate || 0) * 100).toFixed(1).replace('.', ',')}%
                  </td>
                  <td className="text-right py-3 px-4 font-semibold text-red-600">
                    {((page.bounce_rate || 0) * 100).toFixed(1).replace('.', ',')}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ViewMoreButton 
          isExpanded={isExpanded} 
          onClick={onToggleExpand}
          totalItems={sortedData.length}
        />
      </CardContent>
    </Card>
  );
};

// Demographic Table Component with Sorting
const DemographicTable = ({ 
  title, 
  icon, 
  data, 
  labelKey, 
  labelHeader,
  labelFormatter,
  isExpanded, 
  onToggleExpand,
  defaultLimit = 5
}) => {
  const [sortColumn, setSortColumn] = useState('users');
  const [sortDirection, setSortDirection] = useState('desc');
  
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };
  
  const sortedData = [...data].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];
    
    if (sortColumn === 'users') {
      aVal = aVal || 0;
      bVal = bVal || 0;
    } else if (sortColumn === 'conversion_rate') {
      aVal = aVal || 0;
      bVal = bVal || 0;
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });
  
  const displayData = isExpanded ? sortedData : sortedData.slice(0, defaultLimit);
  
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ChevronDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-3 w-3" /> 
      : <ChevronDown className="h-3 w-3" />;
  };
  
  const getLabel = (item) => {
    const value = item[labelKey];
    return labelFormatter ? labelFormatter(value) : value;
  };

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-4 text-sm">
            Não há dados disponíveis.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-2 pb-2 border-b border-border text-xs font-medium text-muted-foreground">
          <div className="col-span-6">{labelHeader}</div>
          <button 
            onClick={() => handleSort('users')}
            className="col-span-3 text-right flex items-center justify-end gap-1 hover:text-foreground transition-colors"
          >
            Usuários
            {getSortIcon('users')}
          </button>
          <button 
            onClick={() => handleSort('conversion_rate')}
            className="col-span-3 text-right flex items-center justify-end gap-1 hover:text-foreground transition-colors"
          >
            Conversão
            {getSortIcon('conversion_rate')}
          </button>
        </div>
        
        {/* Table Body */}
        <div className="divide-y divide-border/40">
          {displayData.map((item, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 py-2.5 hover:bg-muted/30 transition-colors">
              <div className="col-span-6 text-sm truncate" title={getLabel(item)}>
                {getLabel(item)}
              </div>
              <div className="col-span-3 text-right text-sm text-muted-foreground">
                {(item.users || 0).toLocaleString('pt-BR')}
              </div>
              <div className="col-span-3 text-right text-sm font-medium" style={{ color: 'hsl(var(--primary))' }}>
                {((item.conversion_rate || 0) * 100).toFixed(1).replace('.', ',')}%
              </div>
            </div>
          ))}
        </div>
        
        <ViewMoreButton 
          isExpanded={isExpanded} 
          onClick={onToggleExpand}
          totalItems={data.length}
          limit={defaultLimit}
        />
      </CardContent>
    </Card>
  );
};

export const Reports = ({ projectId: projectIdProp, projectName: projectNameProp, apiClient, buildUrl, mode = 'admin' } = {}) => {
  const { selectedProject: ctxSelectedProject } = useApp();
  const navigate = useNavigate();
  const isPortal = mode === 'portal';
  const http = apiClient || axios;
  const portalReports = usePortalReports();
  const usePortalCache = isPortal && !!portalReports;
  const selectedProject = useMemo(
    () => (projectIdProp ? { id: projectIdProp, name: projectNameProp || 'Projeto' } : ctxSelectedProject),
    [projectIdProp, projectNameProp, ctxSelectedProject]
  );
  const reportUrl = buildUrl || ((suffix) => `${API_URL}/api/reports/projects/${selectedProject?.id}/${suffix}`);
  const [loading, setLoading] = useState(true);
  const [globalConfigExists, setGlobalConfigExists] = useState(false);
  const [projectSettings, setProjectSettings] = useState(null);

  // Report data
  const [reportData, setReportData] = useState(null);
  
  // Period filter state - supports preset or custom mode
  const [periodFilter, setPeriodFilter] = useState(() => (isPortal && portalReports?.periodFilter) || {
    mode: 'preset',
    preset: isPortal ? 'mes_atual' : '30',
    startDate: null,
    endDate: null
  });
  
  const [loadingData, setLoadingData] = useState(false);
  const [dataMode, setDataMode] = useState('mock');
  const [connectionError, setConnectionError] = useState(null);
  
  // Funnel data
  const [purchaseFunnel, setPurchaseFunnel] = useState(null);
  const [checkoutFunnel, setCheckoutFunnel] = useState(null);
  const [loadingFunnels, setLoadingFunnels] = useState(false);
  const [funnelErrors, setFunnelErrors] = useState({ purchase: null, checkout: null });

  // Assistant analysis state
  const [assistantAnalysis, setAssistantAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisDateRange, setAnalysisDateRange] = useState(null);
  const [exportingFormat, setExportingFormat] = useState(null);
  
  // Full report export state
  const [exportingFullReport, setExportingFullReport] = useState(false);

  // Table expansion states
  const [tableExpansion, setTableExpansion] = useState({
    devices: false,
    browsers: false,
    channels: false,
    topPages: false,
    regions: false,
    ageGroups: false,
    gender: false
  });

  // Demographics data state
  const [demographicsData, setDemographicsData] = useState(null);
  const [loadingDemographics, setLoadingDemographics] = useState(false);

  // Whether the user has already requested the reports for the current project.
  // Reports must NOT load automatically when entering the page.
  const [hasRequestedReports, setHasRequestedReports] = useState(isPortal);

  // Toggle table expansion
  const toggleTableExpansion = (tableKey) => {
    setTableExpansion(prev => ({
      ...prev,
      [tableKey]: !prev[tableKey]
    }));
  };

  // Helper function to get start and end dates from period filter
  // Uses local date formatting to avoid UTC offset issues
  const getDateRange = useCallback(() => {
    if (periodFilter.mode === 'custom' && periodFilter.startDate && periodFilter.endDate) {
      return {
        startDate: periodFilter.startDate,
        endDate: periodFilter.endDate
      };
    }
    
    if (periodFilter.preset === 'mes_atual') return currentMonthRange();

    // Preset mode - calculate dates relative to today in LOCAL timezone
    const today = new Date();
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    startDate.setDate(startDate.getDate() - parseInt(periodFilter.preset));
    
    // Format as YYYY-MM-DD without UTC conversion
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate)
    };
  }, [periodFilter]);

  // Compute the equivalent previous period (same length, immediately before),
  // mirroring the backend comparison logic. Used only to inform the user.
  const comparisonInfo = useMemo(() => {
    try {
      const { startDate, endDate } = getDateRange();
      if (!startDate || !endDate) return null;
      const start = new Date(`${startDate}T00:00:00`);
      const end = new Date(`${endDate}T00:00:00`);
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return null;
      const msDay = 86400000;
      const lengthDays = Math.round((end - start) / msDay) + 1;
      const prevEnd = new Date(start.getTime() - msDay);
      const prevStart = new Date(prevEnd.getTime() - (lengthDays - 1) * msDay);
      const fmt = (d) => {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${dd}/${mm}/${d.getFullYear()}`;
      };
      return {
        current: `${fmt(start)} a ${fmt(end)}`,
        previous: `${fmt(prevStart)} a ${fmt(prevEnd)}`,
      };
    } catch (e) {
      return null;
    }
  }, [getDateRange]);

  // Handle period filter change
  const handlePeriodChange = useCallback((newFilter) => {
    setPeriodFilter(prev => ({
      ...prev,
      mode: newFilter.mode,
      preset: newFilter.mode === 'preset' ? newFilter.preset : prev.preset,
      startDate: newFilter.mode === 'custom' ? newFilter.startDate : null,
      endDate: newFilter.mode === 'custom' ? newFilter.endDate : null
    }));
  }, []);

  useEffect(() => {
    if (selectedProject) {
      // Reset request state whenever the selected project changes,
      // so reports never auto-load for a freshly selected project.
      setHasRequestedReports(isPortal);
      setReportData(null);
      setDemographicsData(null);
      setPurchaseFunnel(null);
      setCheckoutFunnel(null);
      setAssistantAnalysis(null);
      setAnalysisDateRange(null);
      setConnectionError(null);
      checkConfigurations();
    }
  }, [selectedProject]);

  // Reload report data when period filter changes,
  // but ONLY if the user has already triggered the first load.
  useEffect(() => {
    if (
      selectedProject &&
      projectSettings &&
      projectSettings.is_active &&
      globalConfigExists &&
      hasRequestedReports &&
      !usePortalCache
    ) {
      loadReportData();
    }
  }, [periodFilter]);

  // Portal: shared, persistent cache (auto-loads on period change unless cached)
  const portalRange = usePortalCache ? getDateRange() : null;
  const portalKey = portalRange ? rangeKey(portalRange.startDate, portalRange.endDate) : null;
  useEffect(() => {
    if (usePortalCache && portalRange) portalReports.ensure(portalRange.startDate, portalRange.endDate);
    if (usePortalCache) portalReports.setPeriodFilter(periodFilter);
  }, [portalKey]);
  useEffect(() => {
    if (!usePortalCache) return undefined;
    portalReports.setMetricsVisible(true);
    return () => portalReports.setMetricsVisible(false);
  }, [usePortalCache]);

  const checkConfigurations = async () => {
    setLoading(true);
    try {
      if (isPortal) {
        setGlobalConfigExists(true);
        setProjectSettings({ is_active: true });
        setLoading(false);
        return;
      }
      let globalActive = false;
      
      // Check global config
      try {
        const globalResp = await axios.get(`${API_URL}/api/integrations/ga4/global-settings`);
        globalActive = globalResp.data.is_active;
        setGlobalConfigExists(globalActive);
      } catch (error) {
        setGlobalConfigExists(false);
      }

      // Check project config
      try {
        const projectResp = await axios.get(
          `${API_URL}/api/integrations/projects/${selectedProject.id}/ga4-settings`
        );
        setProjectSettings(projectResp.data);
        // NOTE: report data is no longer auto-loaded here.
        // The user must explicitly request reports (e.g., via the "Carregar relatórios"
        // button or by changing the period after the first load).
      } catch (error) {
        setProjectSettings(null);
      }
    } catch (error) {
      console.error('Error checking configurations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadReports = () => {
    if (usePortalCache) {
      portalReports.ensure(portalRange.startDate, portalRange.endDate);
      return;
    }
    setHasRequestedReports(true);
    loadReportData();
  };

  const loadReportData = async () => {
    setLoadingData(true);
    setConnectionError(null);
    
    try {
      const { startDate, endDate } = getDateRange();

      const params = {
        start_date: startDate,
        end_date: endDate
      };

      // Per-section fetch: one failing section must not take down the others
      const [overviewResp, topPagesResp, devicesResp, browsersResp, sourceMediumResp, demographicsResp] = await Promise.allSettled([
        http.get(reportUrl('usability-overview'), { params }),
        http.get(reportUrl('top-pages'), { params }),
        http.get(reportUrl('device-breakdown'), { params }),
        http.get(reportUrl('browser-breakdown'), { params }),
        http.get(reportUrl('source-medium-breakdown'), { params }),
        http.get(reportUrl('demographics'), { params })
      ]);

      const sectionData = (r) => (r.status === 'fulfilled' && r.value.data?.success ? r.value.data.data : null);

      // usability-overview carries the core KPIs + timeseries → treated as critical
      if (overviewResp.status !== 'fulfilled' || !overviewResp.value.data?.success) {
        const msg = overviewResp.status === 'fulfilled'
          ? (overviewResp.value.data?.error || 'Erro ao carregar dados')
          : (overviewResp.reason?.response?.data?.error || overviewResp.reason?.message || 'Erro ao carregar dados');
        throw new Error(msg);
      }

      setReportData({
        overview: overviewResp.value.data.data,
        topPages: sectionData(topPagesResp),
        devices: sectionData(devicesResp),
        browsers: sectionData(browsersResp),
        sourceMediums: sectionData(sourceMediumResp),
        timeseriesUsers: overviewResp.value.data.data.timeseriesUsers || []
      });

      setDemographicsData(sectionData(demographicsResp));
      setDataMode(overviewResp.value.data.data_mode || 'mock');
      
      // Also load funnels
      await loadFunnelData();
    } catch (error) {
      console.error('Error loading report data:', error);
      
      // Extract meaningful error message
      let errorMessage = 'Não foi possível carregar métricas do GA4.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Portal clients never see technical details or the service-account e-mail
      if (isPortal) {
        errorMessage = 'Não foi possível carregar esta métrica agora';
      }
      
      // Check if it's a "real GA4 not implemented" error
      if (!isPortal && (errorMessage.includes('não foi implementada') || errorMessage.includes('não está implementada'))) {
        setConnectionError({
          type: 'not_implemented',
          message: errorMessage
        });
      } else {
        setConnectionError({
          type: 'error',
          message: errorMessage
        });
      }
      
      toast.error(errorMessage);
    } finally {
      setLoadingData(false);
    }
  };

  const loadFunnelData = async () => {
    setLoadingFunnels(true);
    setFunnelErrors({ purchase: null, checkout: null });
    
    try {
      const { startDate, endDate } = getDateRange();

      const params = {
        start_date: startDate,
        end_date: endDate
      };

      // Load both funnels in parallel
      const [purchaseResp, checkoutResp] = await Promise.all([
        http.get(reportUrl('purchase-funnel'), { params })
          .catch(err => ({ data: { success: false, error: isPortal ? 'Não foi possível carregar esta métrica agora' : (err.response?.data?.error || 'Erro ao carregar funil') } })),
        http.get(reportUrl('checkout-funnel'), { params })
          .catch(err => ({ data: { success: false, error: isPortal ? 'Não foi possível carregar esta métrica agora' : (err.response?.data?.error || 'Erro ao carregar funil') } }))
      ]);

      // Handle purchase funnel
      if (purchaseResp.data.success) {
        setPurchaseFunnel(purchaseResp.data.data);
      } else {
        setFunnelErrors(prev => ({ ...prev, purchase: purchaseResp.data.error }));
      }

      // Handle checkout funnel
      if (checkoutResp.data.success) {
        setCheckoutFunnel(checkoutResp.data.data);
      } else {
        setFunnelErrors(prev => ({ ...prev, checkout: checkoutResp.data.error }));
      }
    } catch (error) {
      console.error('Error loading funnel data:', error);
    } finally {
      setLoadingFunnels(false);
    }
  };

  // Generate assistant analysis
  const generateAssistantAnalysis = async () => {
    if (!selectedProject) return;
    
    setLoadingAnalysis(true);
    
    try {
      const { startDate, endDate } = getDateRange();
      
      const response = await axios.post(
        `${API_URL}/api/reports/projects/${selectedProject.id}/assistant-analysis`,
        {
          start_date: startDate,
          end_date: endDate
        }
      );
      
      if (response.data.success) {
        setAssistantAnalysis(response.data.analysis);
        setAnalysisDateRange({ startDate, endDate });
        toast.success('Análise gerada com sucesso!');
      } else {
        toast.error(response.data.error || 'Erro ao gerar análise');
      }
    } catch (error) {
      console.error('Error generating assistant analysis:', error);
      const errorMessage = error.response?.data?.detail || 
        'Não foi possível gerar a análise do assistente no momento. Tente novamente mais tarde.';
      toast.error(errorMessage);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Check if analysis is outdated (different period)
  const isAnalysisOutdated = () => {
    if (!analysisDateRange) return false;
    const currentRange = getDateRange();
    return analysisDateRange.startDate !== currentRange.startDate || 
           analysisDateRange.endDate !== currentRange.endDate;
  };

  // Export analysis as DOCX or PDF
  const handleExportAnalysis = async (format) => {
    if (!assistantAnalysis || !analysisDateRange) {
      toast.error('Não há análise disponível para exportar.');
      return;
    }

    setExportingFormat(format);

    try {
      const response = await axios.get(
        `${API_URL}/api/reports/projects/${selectedProject.id}/assistant-analysis/export`,
        {
          params: {
            format: format,
            start_date: analysisDateRange.startDate,
            end_date: analysisDateRange.endDate
          },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'pdf' 
          ? 'application/pdf' 
          : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = `analise-assistente-${analysisDateRange.startDate}-${analysisDateRange.endDate}.${format}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Análise exportada como ${format.toUpperCase()} com sucesso!`);

    } catch (error) {
      console.error('Error exporting analysis:', error);
      const errorMessage = error.response?.data?.detail || 
        'Não foi possível exportar a análise. Tente novamente.';
      toast.error(errorMessage);
    } finally {
      setExportingFormat(null);
    }
  };

  // Export full report as PDF
  const handleExportFullReport = async () => {
    if (!selectedProject) {
      toast.error('Nenhum projeto selecionado.');
      return;
    }

    setExportingFullReport(true);

    try {
      const { startDate, endDate } = getDateRange();

      const response = await http.get(
        reportUrl('export-full-report-pdf'),
        {
          params: {
            start_date: startDate,
            end_date: endDate
          },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Create filename
      const safeName = selectedProject.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').substring(0, 30);
      const filename = `relatorio-evolux-${safeName}-${startDate}_a_${endDate}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Relatório exportado como PDF com sucesso!');

    } catch (error) {
      console.error('Error exporting full report:', error);
      const errorMessage = error.response?.data?.detail || 
        'Não foi possível gerar o PDF do relatório no momento. Tente novamente mais tarde.';
      toast.error(errorMessage);
    } finally {
      setExportingFullReport(false);
    }
  };

  const pEntry = usePortalCache ? portalReports.entries[portalKey] : null;
  const pData = (k) => {
    const sec = pEntry?.sections[k];
    return sec?.payload?.success ? sec.payload.data : null;
  };
  const slot = (k) => {
    if (!usePortalCache) return undefined;
    const sec = pEntry?.sections[k];
    return { status: sec?.status || 'queued', payload: sec?.payload, error: sec?.error, onRetry: () => portalReports.retry(portalKey, k) };
  };
  const rd = usePortalCache
    ? (pEntry ? {
        overview: pData('usability-overview'),
        topPages: pData('top-pages'),
        devices: pData('device-breakdown'),
        browsers: pData('browser-breakdown'),
        sourceMediums: pData('source-medium-breakdown'),
        timeseriesUsers: pData('usability-overview')?.timeseriesUsers || []
      } : null)
    : reportData;
  const demo = usePortalCache ? pData('demographics') : demographicsData;
  const pFunnel = usePortalCache ? pData('purchase-funnel') : purchaseFunnel;
  const cFunnel = usePortalCache ? pData('checkout-funnel') : checkoutFunnel;
  const portalBusy = usePortalCache && !!pEntry?.loading;

  if (!selectedProject) {
    return <div className="text-center py-12">Selecione um projeto</div>;
  }

  if (loading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  // Scenario 1: Global not configured
  if (!globalConfigExists) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Relatórios</h1>
          <p className="text-sm text-muted-foreground">Métricas e dados do Google Analytics 4</p>
        </div>

        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-orange-600 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 mb-2">
                  Integração global com o GA4 não configurada
                </h3>
                <p className="text-sm text-orange-800 mb-4">
                  Para usar relatórios neste projeto, configure a integração global com o GA4 
                  em Configurações do sistema.
                </p>
                <Button 
                  onClick={() => navigate('/system-settings')}
                  variant="outline"
                  className="border-orange-300 bg-white hover:bg-orange-100"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Ir para Configurações do sistema
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Scenario 2 & 3: Global configured - Show tabs
  const dashboardVisible = projectSettings && projectSettings.is_active;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Relatórios</h1>
          <p className="text-sm text-muted-foreground">Métricas e análises do Google Analytics 4</p>
          {dashboardVisible && hasRequestedReports && comparisonInfo && (
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="comparison-period-info">
              Período atual:{' '}
              <span className="font-medium text-foreground/80">{comparisonInfo.current}</span>
              <span className="mx-1.5 text-muted-foreground/50">|</span>
              Comparado com:{' '}
              <span className="font-medium text-foreground/80">{comparisonInfo.previous}</span>
            </p>
          )}
          {usePortalCache && pEntry?.loadedAt && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="portal-reports-freshness">
              Dados de {fmtBr(pEntry.startDate)} a {fmtBr(pEntry.endDate)} · atualizado em {fmtBrDateTime(pEntry.loadedAt)}
            </p>
          )}
        </div>
        {dashboardVisible && (
          <div className="flex items-center gap-3">
            <Label className="text-sm">Período:</Label>
            <DateRangePicker
              mode={periodFilter.mode}
              preset={periodFilter.preset}
              startDate={periodFilter.startDate}
              endDate={periodFilter.endDate}
              onChange={handlePeriodChange}
              disabled={loadingData}
              {...(isPortal ? { presets: PORTAL_PRESETS } : {})}
            />
            <Button
              onClick={handleLoadReports}
              disabled={loadingData}
              data-testid="load-reports-btn"
            >
              {loadingData ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Carregando…
                </>
              ) : (
                <>
                  {hasRequestedReports && !isPortal ? 'Atualizar relatórios' : 'Carregar relatórios'}
                </>
              )}
            </Button>
            {usePortalCache && (
              <Button
                variant="outline"
                onClick={() => portalReports.refresh(portalRange.startDate, portalRange.endDate)}
                disabled={portalBusy}
                data-testid="portal-reports-refresh-btn"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${portalBusy ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            )}
            <Button
              onClick={generateAssistantAnalysis}
              disabled={loadingAnalysis || loadingData || !hasRequestedReports || !reportData}
              variant="outline"
              className={`border-primary/30 hover:bg-primary/5 ${isPortal ? 'hidden' : ''}`}
            >
              {loadingAnalysis ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Gerando análise…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4 text-primary" />
                  Análise do assistente
                </>
              )}
            </Button>
            <Button
              onClick={handleExportFullReport}
              disabled={exportingFullReport || loadingData || !hasRequestedReports || !rd}
              variant="outline"
            >
              {exportingFullReport ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Gerando PDF…
                </>
              ) : (
                <>
                  <FileDown className="mr-2 h-4 w-4" />
                  Exportar relatório (PDF)
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* GA4 not configured state */}
      {!dashboardVisible && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 mb-2">
                  Propriedade do GA4 não configurada para este projeto
                </h3>
                <p className="text-sm text-orange-800 mb-4">
                  Configure a integração do GA4 deste projeto para visualizar os relatórios.
                </p>
                <Button 
                  onClick={() => navigate('/integrations')}
                  variant="outline"
                  className="border-orange-300 bg-white hover:bg-orange-100"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Ir para Integrações
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dashboard content when GA4 is configured */}
      {dashboardVisible && (
        <>
          {usePortalCache && <PortalReportsStatus entryKey={portalKey} />}

          {/* Connection error alert */}
          {connectionError && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900 mb-1">
                      {connectionError.type === 'not_implemented' 
                        ? 'Conexão real com o GA4 não está disponível'
                        : 'Não foi possível carregar métricas reais do GA4'}
                    </p>
                    <p className="text-sm text-red-800">
                      {connectionError.message}
                    </p>
                    {connectionError.type === 'not_implemented' && (
                      <Button 
                        onClick={() => navigate('/system-settings')}
                        variant="outline"
                        size="sm"
                        className="mt-3 border-red-300 bg-white hover:bg-red-100 text-red-700"
                      >
                        <Settings className="mr-2 h-3 w-3" />
                        Ir para Configurações do sistema
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty state: user has not requested reports yet */}
          {!connectionError && !hasRequestedReports && !loadingData && (
            <Card data-testid="reports-empty-state" className="border-dashed">
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center text-center max-w-md mx-auto">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <FileText className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold mb-2">
                    Selecione um período para carregar os relatórios
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Escolha o intervalo de datas desejado e clique em
                    {' '}<span className="font-medium text-foreground">Carregar relatórios</span>{' '}
                    para visualizar os dados do GA4 deste projeto.
                  </p>
                  <Button onClick={handleLoadReports} disabled={loadingData} data-testid="empty-state-load-btn">
                    {loadingData ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Carregando…
                      </>
                    ) : (
                      'Carregar relatórios'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Data mode indicator */}
          {!isPortal && !connectionError && hasRequestedReports && reportData && (
            <Card className={dataMode === 'mock' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}>
              <CardContent className="pt-6">
                <p className="text-sm font-medium" style={{ color: dataMode === 'mock' ? '#1e40af' : '#15803d' }}>
                  {dataMode === 'mock' 
                    ? '📊 Exibindo dados simulados (mock) do GA4.'
                    : '✅ Exibindo dados reais do GA4.'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Assistant Analysis Card */}
          {!isPortal && assistantAnalysis && (
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-background">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Análise do assistente
                    </CardTitle>
                    <CardDescription>
                      Resumo automático com base nos dados filtrados do GA4 para este projeto.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAnalysisOutdated() && (
                      <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                        <AlertCircle className="h-3 w-3" />
                        Período alterado
                      </div>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={exportingFormat !== null}
                        >
                          {exportingFormat ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Exportando...
                            </>
                          ) : (
                            <>
                              <Download className="mr-2 h-4 w-4" />
                              Exportar
                            </>
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => handleExportAnalysis('docx')}
                          disabled={exportingFormat !== null}
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Baixar como DOCX
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleExportAnalysis('pdf')}
                          disabled={exportingFormat !== null}
                        >
                          <FileDown className="mr-2 h-4 w-4" />
                          Baixar como PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
                  <ReactMarkdown components={markdownComponents}>
                    {assistantAnalysis}
                  </ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}

          {loadingData && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">Carregando dados...</span>
            </div>
          )}

          {(usePortalCache ? !!rd : (!loadingData && reportData)) && (
            <>
              <ReportSlot section={slot('usability-overview')} minHeight={420} testId="section-overview">{() => (<>
              {/* Main KPIs - Top Priority */}
              <div>
                <h2 className="text-lg font-semibold mb-4">Principais métricas</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <MainKPICard
                    title="Taxa de rejeição"
                    value={rd.overview.bounce_rate}
                    comparisonData={rd.overview.comparison?.bounce_rate}
                    formatter={(val) => `${(val * 100).toFixed(1).replace('.', ',')}%`}
                    isLowerBetter={true}
                    helpKey="bounce_rate"
                  />
                  
                  <MainKPICard
                    title="Taxa de conversão"
                    value={rd.overview.conversion_rate}
                    comparisonData={rd.overview.comparison?.conversion_rate}
                    formatter={(val) => `${(val * 100).toFixed(1).replace('.', ',')}%`}
                    isLowerBetter={false}
                    helpKey="conversion_rate"
                  />
              
              <MainKPICard
                title="Novos usuários"
                value={rd.overview.new_users}
                comparisonData={rd.overview.comparison?.new_users}
                formatter={(val) => val?.toLocaleString('pt-BR')}
                isLowerBetter={false}
                helpKey="new_users"
              />
            </div>
          </div>

          {/* E-commerce KPIs */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Métricas de E-commerce</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <MainKPICard
                title="Total de compras"
                value={rd.overview.total_purchases}
                comparisonData={rd.overview.comparison?.total_purchases}
                formatter={(val) => val?.toLocaleString('pt-BR')}
                isLowerBetter={false}
                helpKey="total_purchases"
              />
              
              <MainKPICard
                title="Receita"
                value={rd.overview.total_revenue}
                comparisonData={rd.overview.comparison?.total_revenue}
                formatter={(val) => `R$ ${val?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                isLowerBetter={false}
                helpKey="total_revenue"
              />
            </div>
          </div>

          {/* Secondary KPI Cards */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Métricas complementares</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    Sessões
                    <MetricHelpIcon metricKey="sessions" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{rd.overview.sessions?.toLocaleString('pt-BR')}</div>
                  {rd.overview.comparison?.sessions && (
                    <p className="text-xs mt-2 flex items-center gap-1">
                      {rd.overview.comparison.sessions.deltaPercent > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : rd.overview.comparison.sessions.deltaPercent < 0 ? (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      ) : (
                        <Minus className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className={rd.overview.comparison.sessions.deltaPercent > 0 ? 'text-green-600' : rd.overview.comparison.sessions.deltaPercent < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                        {rd.overview.comparison.sessions.deltaPercent > 0 ? '+' : ''}{rd.overview.comparison.sessions.deltaPercent?.toFixed(1).replace('.', ',')}%
                      </span>
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    Usuários
                    <MetricHelpIcon metricKey="users" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{rd.overview.users?.toLocaleString('pt-BR')}</div>
                  {rd.overview.comparison?.users && (
                    <p className="text-xs mt-2 flex items-center gap-1">
                      {rd.overview.comparison.users.deltaPercent > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : rd.overview.comparison.users.deltaPercent < 0 ? (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      ) : (
                        <Minus className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className={rd.overview.comparison.users.deltaPercent > 0 ? 'text-green-600' : rd.overview.comparison.users.deltaPercent < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                        {rd.overview.comparison.users.deltaPercent > 0 ? '+' : ''}{rd.overview.comparison.users.deltaPercent?.toFixed(1).replace('.', ',')}%
                      </span>
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    Taxa de engajamento
                    <MetricHelpIcon metricKey="engagement_rate" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{(rd.overview.engagement_rate * 100).toFixed(1).replace('.', ',')}%</div>
                  {rd.overview.comparison?.engagement_rate && (
                    <p className="text-xs mt-2 flex items-center gap-1">
                      {rd.overview.comparison.engagement_rate.deltaPercent > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : rd.overview.comparison.engagement_rate.deltaPercent < 0 ? (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      ) : (
                        <Minus className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className={rd.overview.comparison.engagement_rate.deltaPercent > 0 ? 'text-green-600' : rd.overview.comparison.engagement_rate.deltaPercent < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                        {rd.overview.comparison.engagement_rate.deltaPercent > 0 ? '+' : ''}{rd.overview.comparison.engagement_rate.deltaPercent?.toFixed(1).replace('.', ',')}%
                      </span>
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    Tempo médio de engajamento
                    <MetricHelpIcon metricKey="avg_engagement_time" />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatTime(rd.overview.avg_engagement_time)}</div>
                  {rd.overview.comparison?.avg_engagement_time && (
                    <p className="text-xs mt-2 flex items-center gap-1">
                      {rd.overview.comparison.avg_engagement_time.deltaPercent > 0 ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : rd.overview.comparison.avg_engagement_time.deltaPercent < 0 ? (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      ) : (
                        <Minus className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className={rd.overview.comparison.avg_engagement_time.deltaPercent > 0 ? 'text-green-600' : rd.overview.comparison.avg_engagement_time.deltaPercent < 0 ? 'text-red-600' : 'text-muted-foreground'}>
                        {rd.overview.comparison.avg_engagement_time.deltaPercent > 0 ? '+' : ''}{rd.overview.comparison.avg_engagement_time.deltaPercent?.toFixed(1).replace('.', ',')}%
                      </span>
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Users over time chart with previous period comparison */}
          {rd.timeseriesUsers && rd.timeseriesUsers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Usuários ao longo do tempo</CardTitle>
                <CardDescription>Comparação entre período atual e período anterior</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={rd.timeseriesUsers}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        // Parse YYYY-MM-DD as local date to avoid UTC offset
                        const parts = value.split('-');
                        const day = parseInt(parts[2], 10);
                        const month = parseInt(parts[1], 10);
                        return `${day}/${month}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }}
                      labelFormatter={(value) => {
                        // Parse YYYY-MM-DD as local date to avoid UTC offset
                        const parts = value.split('-');
                        const day = parts[2];
                        const month = parts[1];
                        const year = parts[0];
                        return `${day}/${month}/${year}`;
                      }}
                      formatter={(value, name) => {
                        if (name === 'current') return [value.toLocaleString('pt-BR') + ' usuários', 'Período atual'];
                        if (name === 'previous') return [value.toLocaleString('pt-BR') + ' usuários', 'Período anterior'];
                        return [value, name];
                      }}
                    />
                    <Legend 
                      formatter={(value) => {
                        if (value === 'current') return 'Período atual';
                        if (value === 'previous') return 'Período anterior';
                        return value;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="current" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2.5}
                      name="current"
                      dot={{ fill: 'hsl(var(--primary))', r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="previous" 
                      stroke="#94a3b8" 
                      strokeWidth={1.5}
                      strokeDasharray="5 5"
                      name="previous"
                      dot={{ fill: '#94a3b8', r: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          </>)}</ReportSlot>

          <ReportSlot section={slot('device-breakdown')} testId="section-devices">{() => (<>
          {/* Device breakdown chart */}
          <Card>
            <CardHeader>
              <CardTitle>Distribuição por dispositivo</CardTitle>
              <CardDescription>Sessões por tipo de dispositivo</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={rd.devices}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="device_category" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px'
                    }}
                    formatter={(value, name) => {
                      if (name === 'sessions') return [value.toLocaleString(), 'Sessões'];
                      if (name === 'engagement_rate') return [(value * 100).toFixed(1) + '%', 'Taxa de engajamento'];
                      return [value, name];
                    }}
                  />
                  <Legend 
                    formatter={(value) => {
                      if (value === 'sessions') return 'Sessões';
                      if (value === 'engagement_rate') return 'Taxa de engajamento';
                      return value;
                    }}
                  />
                  <Bar dataKey="sessions" fill="hsl(var(--primary))" name="sessions" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Detail Table: UX Metrics by Device */}
          <DeviceBreakdownTable 
            devices={rd.devices} 
            formatTime={formatTime} 
            isExpanded={tableExpansion.devices}
            onToggleExpand={() => toggleTableExpansion('devices')}
          />

          </>)}</ReportSlot>

          {/* Detail Table: UX Metrics by Browser */}
          <ReportSlot section={slot('browser-breakdown')} testId="section-browsers">{() => (
          <BrowserBreakdownTable 
            browsers={rd.browsers} 
            formatTime={formatTime}
            isExpanded={tableExpansion.browsers}
            onToggleExpand={() => toggleTableExpansion('browsers')}
          />
          )}</ReportSlot>

          {/* Detail Table: UX Metrics by Source/Medium */}
          <ReportSlot section={slot('source-medium-breakdown')} testId="section-sources">{() => (
          <SourceMediumBreakdownTable 
            sourceMediums={rd.sourceMediums} 
            formatTime={formatTime}
            isExpanded={tableExpansion.channels}
            onToggleExpand={() => toggleTableExpansion('channels')}
          />
          )}</ReportSlot>

          {/* Top pages table */}
          <ReportSlot section={slot('top-pages')} testId="section-top-pages">{() => (
          <TopPagesTable 
            topPages={rd.topPages} 
            formatTime={formatTime}
            isExpanded={tableExpansion.topPages}
            onToggleExpand={() => toggleTableExpansion('topPages')}
          />
          )}</ReportSlot>

          {/* Demographics Section */}
          <ReportSlot section={slot('demographics')} testId="section-demographics">{() => demo && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Dados demográficos
                </h2>
                <p className="text-sm text-muted-foreground">
                  Distribuição dos usuários por região, faixa etária e gênero.
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Region Table */}
                <DemographicTable
                  title="Região"
                  icon={<MapPin className="h-4 w-4" />}
                  data={demo.regions || []}
                  labelKey="region"
                  labelHeader="Região"
                  isExpanded={tableExpansion.regions}
                  onToggleExpand={() => toggleTableExpansion('regions')}
                  defaultLimit={5}
                />

                {/* Age Group Table */}
                <DemographicTable
                  title="Faixa etária"
                  data={demo.age_groups || []}
                  labelKey="age_group"
                  labelHeader="Faixa etária"
                  isExpanded={tableExpansion.ageGroups}
                  onToggleExpand={() => toggleTableExpansion('ageGroups')}
                  defaultLimit={10}
                />

                {/* Gender Table */}
                <DemographicTable
                  title="Gênero"
                  data={demo.gender || []}
                  labelKey="gender"
                  labelHeader="Gênero"
                  labelFormatter={(val) => val === 'female' ? 'Feminino' : val === 'male' ? 'Masculino' : 'Não informado'}
                  isExpanded={tableExpansion.gender}
                  onToggleExpand={() => toggleTableExpansion('gender')}
                  defaultLimit={10}
                />
              </div>
            </div>
          )}</ReportSlot>

          {/* Funnels section */}
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Funis de conversão (GA4)</h2>
              <p className="text-sm text-muted-foreground">
                Visualize a jornada dos usuários até a compra e durante o checkout.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ReportSlot section={slot('purchase-funnel')} testId="section-purchase-funnel">{() => (
              <FunnelChart
                title="Funil de Compra"
                data={pFunnel}
                loading={usePortalCache ? false : loadingFunnels}
                error={usePortalCache ? null : funnelErrors.purchase}
                colorScheme="purple"
              />
              )}</ReportSlot>
              
              <ReportSlot section={slot('checkout-funnel')} testId="section-checkout-funnel">{() => (
              <FunnelChart
                title="Funil do Checkout"
                data={cFunnel}
                loading={usePortalCache ? false : loadingFunnels}
                error={usePortalCache ? null : funnelErrors.checkout}
                colorScheme="green"
              />
              )}</ReportSlot>
            </div>
          </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
